import { useEffect, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Tooltip } from 'react-tooltip';
import Common from '../shared/Common';
import type { IDashboardElement } from '../../interfaces/IDashboardElement';
import './style.css';
import type { ICategoryName } from '../../interfaces/ICategoryName';

export interface IDashboardCardProps {
  sortByPrice: boolean;
  showQuantities: boolean;
  showCategories: boolean;
  showGroups: boolean;
  data: IDashboardElement | null;
  onClick: React.MouseEventHandler<HTMLInputElement>;
  id: number;
}

const generateTimeString = (date: Date) => {
  let diffSeconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSeconds < 0) {
    diffSeconds = 0;
  }
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffSeconds < 60) {
    return `${diffSeconds} sek.`;
  }

  if (diffMinutes < 60) {
    return `${diffMinutes} min.`;
  }

  if (diffHours < 24) {
    return `${diffHours} godz.`;
  }

  if (diffDays < 2) {
    return '1 dzień';
  }

  if (diffDays < 7) {
    return `${diffDays} dni`;
  }

  if (diffWeeks < 5) {
    return `${diffWeeks} tyg.`;
  }

  if (diffMonths < 12) {
    return `${diffMonths} mies.`;
  }

  if (diffYears < 2) {
    return '1 rok';
  }

  if (diffYears < 4) {
    return `${diffYears} lata`;
  }

  return `${diffYears} lat`;
};

const generateDateString = (date: Date) => {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const dayString = (day < 10 ? '0' : '') + day.toString();
  const monthString = (month < 10 ? '0' : '') + month.toString();
  const yearString = year.toString();
  return `${dayString}.${monthString}.${yearString}`;
};

interface IDashboardCardElement {
  isIncome: boolean;
  id?: number;
  description?: string;
  ammount?: number;
  quantity?: number;
  category: ICategoryName;
  vatPercentage?: number;
  groupFullName?: string;
  quantityTypeShortName?: string;
}


const generateDateWithTimeString = (date: Date) => {
  const minutes = date.getMinutes();
  const hours = date.getHours();
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const minutesString = (minutes < 10 ? '0' : '') + minutes.toString();
  const hoursString = (hours < 10 ? '0' : '') + hours.toString();
  const dayString = (day < 10 ? '0' : '') + day.toString();
  const monthString = (month < 10 ? '0' : '') + month.toString();
  const yearString = year.toString();
  return `${dayString}.${monthString}.${yearString} ${hoursString}:${minutesString}`;
};

const DashboardCard = (props: IDashboardCardProps) => {
  const [showAllItems, setShowAllItems] = useState<boolean>(false);
  const [pictureIsVertical, setPictureIsVertical] = useState<boolean>(false);
  const elementPicture: string | undefined = props.data?.elementPicture;
  const isIncome = props.data?.type === 'I';

  useEffect(() => {
    const setPictureOrientation = async () => {
      if (elementPicture) {
        const isPictureVertical = await Common.Images.isPictureVertical(elementPicture);
        setPictureIsVertical(isPictureVertical);
      }
    };
    setPictureOrientation();
  }, [elementPicture]);

  const generateDashboardContent = (items: IDashboardCardElement[]) => {
    const maxElementsInList = 6;
    const tooManyElementsInList = items.length > maxElementsInList;
    let currentIndex = 0;
    const itemsToPresent = items.slice();
    if (props.sortByPrice) {
      itemsToPresent.sort((a, b) => (b.ammount ?? 0) - (a.ammount ?? 0));
    } else {
      itemsToPresent.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
    }
    const billItemsList = [];
    for (
      ;
      currentIndex < (tooManyElementsInList && !showAllItems ? maxElementsInList : itemsToPresent.length);
      currentIndex++
    ) {
      let ammount = (Math.round((itemsToPresent[currentIndex].ammount ?? 0) * 100) / 100);
      if (itemsToPresent[currentIndex].isIncome && (itemsToPresent[currentIndex]?.quantity ?? 0) !== 0) {
        ammount = ammount / (itemsToPresent[currentIndex]?.quantity ?? 1);
      }
      billItemsList.push(
        <li
          className='list-group-item py-1 d-flex justify-content-between align-items-center dashboard-card-list-element'
          key={currentIndex}
        >
          <Container>
            <Row className='p-0'>
              <span className='me-1 g-0'>
                {(itemsToPresent[currentIndex].description?.length ?? 0) > 250
                  ? itemsToPresent[currentIndex].description?.substring(0, 250).concat('...')
                  : itemsToPresent[currentIndex].description}
              </span>
            </Row>
            <Row>
              {props.showCategories &&
                (itemsToPresent[currentIndex].category ? (
                  <span
                    className='badge dashboard-card-badge-category me-1'
                    title={itemsToPresent[currentIndex].category?.categoryFullName}
                  >
                    {itemsToPresent[currentIndex].category?.categoryName}
                  </span>
                ) : (
                  <span title='Brak kategorii' className='badge dashboard-card-badge-empty-category me-1'>{'Brak'}</span>
                ))}
              {itemsToPresent[currentIndex].vatPercentage && (
                <span
                  className='badge dashboard-card-badge-vat'
                  title={`Odliczono ${(itemsToPresent[currentIndex].vatPercentage ?? 0) * 100}% podatku VAT`}
                >
                  {`${(itemsToPresent[currentIndex].vatPercentage ?? 0) * 100}% VAT`}
                </span>
              )}
              {props.showGroups && (
                <span className='badge dashboard-card-badge-group'>{itemsToPresent[currentIndex].groupFullName}</span>
              )}
            </Row>
          </Container>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span className='badge dashboard-card-badge-price ms-1 px-2 py-1'>
              {Common.Utils.getCurrencyString(ammount.toFixed(2))}
            </span>
            {itemsToPresent[currentIndex].quantityTypeShortName !== 'NA' && props.showQuantities && (
              <span
                className={`badge dashboard-card-badge-${itemsToPresent[currentIndex].isIncome ? 'income-' : ''}quantity ms-1 px-2 py-1`}
                style={{ marginTop: '2px' }}
              >
                {Number.isInteger(itemsToPresent[currentIndex].quantity ?? 0)
                  ? (itemsToPresent[currentIndex].quantity ?? 0)
                  : (Math.round((itemsToPresent[currentIndex].quantity ?? 0) * 100) / 100).toFixed(2)}
                &nbsp;
                {itemsToPresent[currentIndex].quantityTypeShortName}
              </span>
            )}
          </div>
        </li>
      );
    }
    if (tooManyElementsInList) {
      billItemsList.push(
        <button
          className='list-group-item list-group-item-action'
          key={currentIndex}
          onClick={(e: any) => {
            e.stopPropagation();
            setShowAllItems(!showAllItems);
          }}
        >
          {showAllItems ? 'Pokaż mniej...' : 'Pokaż więcej...'}
        </button>
      );
    }
    return (
      <ul style={{ width: '100%' }} className='list-group'>
        {billItemsList}
      </ul>
    );
  };

  return (
    <>
      <Container
        className={`${isIncome ? 'dashboard-income-card' : 'dashboard-card'} container-fluid`}
        style={props.data ? { borderRadius: '5px' } : { backgroundColor: 'transparent' }}
        fluid
      >
        {!props.data ? (
          <table className='table dashboard-card-table'>
            <tbody>{Common.Ui.getSkeletonTableRows(1, 1, 400)}</tbody>
          </table>
        ) : (
          <Row className='g-0 justify-content-center' onClick={props.onClick}>
            <Col>
              <div className='d-flex h-100 flex-column'>
                <Row
                  className='g-0'
                  style={{
                    flexGrow: 0,
                    paddingTop: '10px',
                    height: '60px',
                  }}
                >
                  <Col
                    style={{
                      flex: '55px 0',
                      paddingLeft: '10px',
                    }}
                  >
                    <img
                      src={
                        !props.data.location?.picture
                          ? `/Static/icons/dashboard-navigation/${isIncome ? 'incomes' : 'bills'}.png`
                          : props.data.location?.picture
                      }
                      style={{
                        height: '36px',
                        width: '36px',
                      }}
                      alt=''
                    />
                  </Col>
                  <Col>
                    <Row
                      className='g-0'
                      style={{
                        color: '#050505',
                        fontSize: '15px',
                        borderTopLeftRadius: '5px',
                        fontWeight: 600,
                      }}
                    >
                      {isIncome ? 'Przychód' : 'Rachunek'} nr {props.id} z dnia{' '}
                      {generateDateString(new Date(props.data.time as string))}
                    </Row>
                    <Row
                      className='g-0'
                      style={{
                        color: '#385898',
                        fontSize: '13px',
                        fontWeight: 400,
                      }}
                    >
                      <span className='dashboard-card-date' id={'dateTip-' + props.data.id}>
                        {props.data.location?.locationName ?? 'Nieznana'} ·{' '}
                        {generateTimeString(new Date(props.data.creationTime + 'Z'))} temu
                      </span>
                      <Tooltip
                        content={'Dodano ' + generateDateWithTimeString(new Date(props.data.creationTime + 'Z'))}
                        className='dashboard-card-date-tooltip'
                        anchorId={'dateTip-' + props.data.id}
                        place='bottom'
                      />
                    </Row>
                  </Col>
                </Row>
                <Row className='half-containers g-0' style={{ flexGrow: 1 }}>
                    {isIncome ? generateDashboardContent([{
                      isIncome: true,
                      id: props.data.incomeId,
                      description: props.data.incomeDescription,
                      ammount: props.data.incomeRevenue,
                      quantity: props.data.incomeQuantity,
                      quantityTypeShortName: props.data.incomeQuantityType?.incomeQuantityTypeShortName,
                      category: {
                        categoryName: props.data.incomeCategoryName,
                        categoryFullName: props.data.incomeCategoryName
                      }
                    } as IDashboardCardElement])
                      : generateDashboardContent(props.data.billItems.map(e => ({
                        isIncome: false,
                        id: e.billItemId,
                        description: e.description,
                        vatPercentage: e.vatPercentage,
                        ammount: e.cost,
                        quantity: e.quantity,
                        quantityTypeShortName: e.quantityType?.shortName,
                        category: e.category,
                        groupFullName: e.group?.groupFullName,
                      } as IDashboardCardElement)))}
                </Row>
                <Row
                  className='half-containers g-0'
                  style={{
                    flexGrow: 0,
                    borderBottomLeftRadius: '5px',
                    color: '#050505',
                    fontSize: '15px',
                    borderTopLeftRadius: '5px',
                    fontWeight: 600,
                  }}
                >
                  <ul style={{ width: '100%' }} className='list-group'>
                    <h5>
                      <li className='list-group-item d-flex justify-content-between align-items-center'>
                        Razem
                        <span className='badge dashboard-card-badge-price ms-1 px-2'>
                          {Common.Utils.getCurrencyString(
                            isIncome ? props.data.incomeRevenue : 
                            (
                              Math.round(
                                props.data.billItems.reduce((partialSum, a) => partialSum + (a.cost ?? 0), 0) * 100
                              ) / 100
                            ).toFixed(2)
                          )}
                        </span>
                      </li>
                    </h5>
                  </ul>
                </Row>
              </div>
            </Col>
            {elementPicture && (
              <Col
                md={pictureIsVertical ? 6 : 12}
                className={
                  pictureIsVertical ? 'dashboard-card-vertical-image-column' : 'dashboard-card-horizontal-image-column'
                }
              >
                <Row className='g-0'>
                  <img
                    className={
                      pictureIsVertical
                        ? 'dashboard-card-vertical-image-column'
                        : 'dashboard-card-horizontal-image-column'
                    }
                    src={elementPicture}
                    alt=''
                  />
                </Row>
              </Col>
            )}
          </Row>
        )}
      </Container>
    </>
  );
};

export default DashboardCard;
